import { createClient, SupabaseClient, RealtimeChannel } from '@supabase/supabase-js';
import { Note, NoteFormData } from '../types';

// --- Configuration ---

// We prioritize environment variables, but fall back to the provided credentials
// if the build environment fails to inject .env variables (common in some sandbox environments).
const getConfig = (key: string, fallback: string): string => {
  try {
    // @ts-ignore
    if (import.meta && import.meta.env && import.meta.env[key]) {
      // @ts-ignore
      return import.meta.env[key];
    }
  } catch (e) {
    // Ignore access errors
  }
  return fallback;
};

const SUPABASE_URL = getConfig('VITE_SUPABASE_URL', 'https://qnmrprpwzhihhlkqcolt.supabase.co');
const SUPABASE_KEY = getConfig('VITE_SUPABASE_ANON_KEY', 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFubXJwcnB3emhpaGhsa3Fjb2x0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjkwNTE2NTYsImV4cCI6MjA4NDYyNzY1Nn0.KTrYw4jszULymH7FsTsX-aR8dM8AZgrPGQ_zweidNRc');

// Check if Supabase is configured (Should always be true now with fallbacks)
const isSupabaseConfigured = !!(SUPABASE_URL && SUPABASE_KEY);

let supabase: SupabaseClient | null = null;

if (isSupabaseConfigured) {
  try {
    supabase = createClient(SUPABASE_URL, SUPABASE_KEY);
  } catch (err) {
    console.error('Failed to create Supabase client:', err);
  }
} else {
  console.error('CRITICAL: Supabase credentials missing entirely.');
}

// Helper to map DB result to Note type safely
const mapDocToNote = (doc: any): Note => {
  return {
    id: doc.id,
    title: doc.title || '',
    content: doc.content || '',
    color: doc.color || 'white',
    // Check both camelCase (quoted identifier) and lowercase (unquoted)
    isFavorite: doc.isFavorite ?? doc.isfavorite ?? false, 
    createdAt: doc.created_at,
    updatedAt: doc.updated_at
  };
};

// Helper to ensure database is ready
const ensureSupabase = () => {
  if (!supabase) {
    throw new Error("Database connection is not available. Check your configuration.");
  }
  return supabase;
};

// --- API Methods ---

export const getNotes = async (): Promise<Note[]> => {
  const client = ensureSupabase();
  
  const { data, error } = await client
    .from('notes')
    .select('*')
    .order('updated_at', { ascending: false });
  
  if (error) {
    console.error('Supabase fetch error:', error.message, error.details);
    throw error;
  }
  
  return (data || []).map(mapDocToNote); 
};

export const createNote = async (noteData: NoteFormData): Promise<Note> => {
  const client = ensureSupabase();
  const now = new Date().toISOString();
  
  const payload = { 
    title: noteData.title, 
    content: noteData.content, 
    color: noteData.color, 
    "isFavorite": noteData.isFavorite, 
    created_at: now, 
    updated_at: now
  };

  const { data, error } = await client
    .from('notes')
    .insert([payload])
    .select()
    .single();

  if (error) {
    console.error('Supabase create error:', error.message, error.details);
    throw error;
  }
  return mapDocToNote(data);
};

export const updateNote = async (id: string, updates: Partial<NoteFormData>): Promise<Note> => {
  const client = ensureSupabase();
  const now = new Date().toISOString();

  const dbUpdates: any = { updated_at: now };
  if (updates.title !== undefined) dbUpdates.title = updates.title;
  if (updates.content !== undefined) dbUpdates.content = updates.content;
  if (updates.color !== undefined) dbUpdates.color = updates.color;
  if (updates.isFavorite !== undefined) dbUpdates["isFavorite"] = updates.isFavorite;

  const { data, error } = await client
    .from('notes')
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Supabase update error:', error.message, error.details);
    throw error;
  }
  return mapDocToNote(data);
};

export const deleteNote = async (id: string): Promise<void> => {
  const client = ensureSupabase();
  
  const { error } = await client
    .from('notes')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Supabase delete error:', error.message);
    throw error;
  }
};

export const toggleFavorite = async (note: Note): Promise<Note> => {
  return await updateNote(note.id, { isFavorite: !note.isFavorite });
};

// --- Realtime Subscription ---

export const subscribeToNotes = (onUpdate: () => void): RealtimeChannel | null => {
  if (supabase) {
    return supabase
      .channel('public:notes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notes' },
        () => {
          onUpdate();
        }
      )
      .subscribe();
  }
  return null;
};